import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Signature } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectIsOrgOwnerOrAdmin } from "../../features/authentication/authenticationSlice";
import { Enrolment } from "../../features/training/models/Enrolment";
import {
  removeEnrolmentSignature,
  signEnrolment,
} from "../../features/training/slice-extensions/training-signature-slice-extension";
import { formatDateOnly } from "../../features/training/utils/formatDateOnly";
import { getDisplayLocale } from "../../features/utils/timezoneUtils";
import SignatureSheet from "./SignatureSheet";
import { RECORD_NOT_AVAILABLE } from "../../features/training/utils/trainingRecordErrors";

interface EnrolmentSignatureControlProps {
  memberId: string;
  enrolment: Enrolment;
}

/**
 * The member's signature on one enrolment's training record: collect it on this screen
 * (hand the device over), see when it was given, sign again, or (Owner/Admin) remove it.
 */
const EnrolmentSignatureControl: React.FC<EnrolmentSignatureControlProps> = ({ memberId, enrolment }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const canManage = useAppSelector(selectIsOrgOwnerOrAdmin);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sign = async (imagePngBase64: string): Promise<boolean> => {
    const result = await dispatch(
      signEnrolment({ memberId, enrolmentId: enrolment.id, request: { imagePngBase64 } })
    );
    if (signEnrolment.fulfilled.match(result)) {
      setOpen(false);
      return true;
    }
    if (result.payload?.code === RECORD_NOT_AVAILABLE) {
      // Signing again cannot help: close the sheet and say why.
      setOpen(false);
      setError(t("training.record.notAvailable"));
    }
    return false;
  };

  const remove = async (): Promise<void> => {
    setError(null);
    const result = await dispatch(removeEnrolmentSignature({ memberId, enrolmentId: enrolment.id }));
    if (!removeEnrolmentSignature.fulfilled.match(result)) {
      setError(
        result.payload?.code === RECORD_NOT_AVAILABLE
          ? t("training.record.notAvailable")
          : result.payload?.message ?? t("training.signature.removeFailed")
      );
    }
  };

  const linkClass = "inline-flex items-center gap-1 text-sm text-primary-40 dark:text-primary-90 hover:underline";

  return (
    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
      {enrolment.signedOn ? (
        <>
          <span className="inline-flex items-center gap-1 text-sm text-grey-50 dark:text-grey-70">
            <Signature size={16} />
            <span>{t("training.signature.signedOn", { date: formatDateOnly(enrolment.signedOn, getDisplayLocale()) })}</span>
          </span>
          <button type="button" className={linkClass} onClick={() => setOpen(true)}>
            {t("training.signature.signAgain")}
          </button>
          {canManage && (
            <button
              type="button"
              className="text-sm text-grey-50 dark:text-grey-70 hover:underline"
              onClick={remove}
            >
              {t("training.signature.remove")}
            </button>
          )}
        </>
      ) : (
        <button type="button" className={linkClass} onClick={() => setOpen(true)}>
          <Signature size={16} />
          <span>{t("training.signature.collect")}</span>
        </button>
      )}
      {error && <p className="w-full text-sm text-red-600 dark:text-red-400">{error}</p>}
      {open && (
        <SignatureSheet subject={enrolment.programmeName} onSign={sign} onClose={() => setOpen(false)} />
      )}
    </div>
  );
};

export default EnrolmentSignatureControl;
